import { reactive } from 'vue';
import md5 from 'md5';
import InboxesAPI from 'dashboard/api/inboxes';

const DB_NAME = 'lola_browser_media_sync';
const DB_VERSION = 3;
const MEDIA_STORE = 'media';
const MESSAGE_STORE = 'messages';
const FOLDER_STORE = 'folders';
const SYNC_STORE = 'sync';
const DEVICE_STORE = 'device';
const CONVERSATION_FOLDER_STORE = 'conversationFolders';
const SYNC_INTERVAL = 5 * 60 * 1000;
const MANIFEST_LIMIT = 200;

export const localMediaUrls = reactive({});
export const syncSummaries = reactive({});
export const mediaSyncDeviceStates = reactive({});

const inFlightAttachments = new Set();
const syncedInboxes = new Map();
const syncInFlight = new Map();
const syncTimers = new Map();
const completedAttachmentStorage = new Map();
let dbPromise = null;

const openDatabase = () => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = event => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(MEDIA_STORE)) {
        db.createObjectStore(MEDIA_STORE, { keyPath: 'attachmentId' });
      }
      if (!db.objectStoreNames.contains(MESSAGE_STORE)) {
        db.createObjectStore(MESSAGE_STORE, { keyPath: 'messageId' });
      }
      if (!db.objectStoreNames.contains(FOLDER_STORE)) {
        db.createObjectStore(FOLDER_STORE, { keyPath: 'inboxId' });
      }
      if (!db.objectStoreNames.contains(SYNC_STORE)) {
        db.createObjectStore(SYNC_STORE, { keyPath: 'inboxId' });
      }
      if (!db.objectStoreNames.contains(DEVICE_STORE)) {
        db.createObjectStore(DEVICE_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(CONVERSATION_FOLDER_STORE)) {
        db.createObjectStore(CONVERSATION_FOLDER_STORE, { keyPath: 'key' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return dbPromise;
};

const requestToPromise = request =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const transactionToPromise = transaction =>
  new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });

const getRecord = async (storeName, key) => {
  if (typeof indexedDB === 'undefined') return undefined;

  const db = await openDatabase();
  return requestToPromise(
    db.transaction(storeName).objectStore(storeName).get(key)
  );
};

const getAllRecords = async storeName => {
  if (typeof indexedDB === 'undefined') return [];

  const db = await openDatabase();
  return requestToPromise(
    db.transaction(storeName).objectStore(storeName).getAll()
  );
};

const putRecord = async (storeName, record) => {
  if (typeof indexedDB === 'undefined') return undefined;

  const db = await openDatabase();
  return requestToPromise(
    db.transaction(storeName, 'readwrite').objectStore(storeName).put(record)
  );
};

const putRecords = async (storeName, records) => {
  if (!records.length) return undefined;
  if (typeof indexedDB === 'undefined') return undefined;

  const db = await openDatabase();
  const transaction = db.transaction(storeName, 'readwrite');
  records.forEach(record => transaction.objectStore(storeName).put(record));
  await transactionToPromise(transaction);
  return undefined;
};

export const isFileSystemAccessSupported = () =>
  typeof window !== 'undefined' && 'showDirectoryPicker' in window;

const createDeviceId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();

  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  // eslint-disable-next-line no-bitwise
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  // eslint-disable-next-line no-bitwise
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map(byte => byte.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
    .slice(6, 8)
    .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
};

export const getMediaSyncDeviceId = async () => {
  const existing = await getRecord(DEVICE_STORE, 'current');
  if (existing?.deviceId) return existing.deviceId;

  const deviceId = createDeviceId();
  await putRecord(DEVICE_STORE, { id: 'current', deviceId });
  return deviceId;
};

const normalizeAttachment = attachment => ({
  attachmentId:
    attachment.attachmentId || attachment.attachment_id || attachment.id,
  messageId: attachment.messageId || attachment.message_id,
  conversationId: attachment.conversationId || attachment.conversation_id,
  inboxId: attachment.inboxId || attachment.inbox_id,
  inboxName: attachment.inboxName || attachment.inbox_name,
  contactName: attachment.contactName || attachment.contact_name,
  contactPhone: attachment.contactPhone || attachment.contact_phone,
  contactEmail: attachment.contactEmail || attachment.contact_email,
  fileType: attachment.fileType || attachment.file_type,
  fileName: attachment.fileName || attachment.file_name,
  extension: attachment.extension,
  contentType: attachment.contentType || attachment.content_type,
  byteSize: attachment.byteSize || attachment.byte_size || attachment.fileSize,
  checksum: attachment.checksum,
  dataUrl: attachment.dataUrl || attachment.data_url,
  downloadUrl:
    attachment.downloadUrl ||
    attachment.download_url ||
    attachment.dataUrl ||
    attachment.data_url,
  thumbUrl: attachment.thumbUrl || attachment.thumb_url,
  createdAt: attachment.createdAt || attachment.created_at,
  updatedAt: attachment.updatedAt || attachment.updated_at,
});

const objectUrlForBlob = (attachmentId, blob) => {
  if (localMediaUrls[attachmentId]) return localMediaUrls[attachmentId];

  const objectUrl = URL.createObjectURL(blob);
  localMediaUrls[attachmentId] = objectUrl;
  return objectUrl;
};

const safeFileName = attachment => {
  const extension = attachment.extension ? `.${attachment.extension}` : '';
  const originalName =
    attachment.fileName || `attachment-${attachment.attachmentId}${extension}`;
  const sanitizedName = originalName
    .replace(/[<>:"/\\|?*]/g, '_')
    .split('')
    .map(char => (char.charCodeAt(0) < 32 ? '_' : char))
    .join('')
    .replace(/[. ]+$/g, '');
  return `${attachment.attachmentId}-${sanitizedName}`.slice(0, 180);
};

export const sanitizeMediaFolderPart = (value, fallback) => {
  const sanitized = String(value || fallback)
    .replace(/[<>:"/\\|?*]/g, '_')
    .split('')
    .map(char => (char.charCodeAt(0) < 32 ? '_' : char))
    .join('')
    .replace(/[. ]+$/g, '')
    .trim()
    .slice(0, 80);
  const reserved = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i;
  return reserved.test(sanitized) ? `_${sanitized}` : sanitized || fallback;
};

export const buildMediaConversationFolderName = attachment => {
  const contactName =
    attachment.contactName ||
    attachment.contactPhone ||
    attachment.contactEmail ||
    'Không rõ liên hệ';
  return `${sanitizeMediaFolderPart(
    attachment.inboxName,
    'Inbox'
  )} - ${sanitizeMediaFolderPart(
    contactName,
    'Không rõ liên hệ'
  )} - ${attachment.conversationId}`;
};

const conversationFolderKey = (inboxId, conversationId) =>
  `${Number(inboxId)}:${Number(conversationId)}`;

const findExistingConversationFolder = async (rootHandle, conversationId) => {
  if (!rootHandle?.values) return '';

  const suffix = ` - ${conversationId}`;
  // File System Access directory iteration is asynchronous.
  // eslint-disable-next-line no-restricted-syntax
  for await (const entry of rootHandle.values()) {
    if (entry.kind === 'directory' && entry.name.endsWith(suffix)) {
      return entry.name;
    }
  }
  return '';
};

const getConversationFolderName = async (rootHandle, attachment) => {
  const key = conversationFolderKey(
    attachment.inboxId,
    attachment.conversationId
  );
  const existing = await getRecord(CONVERSATION_FOLDER_STORE, key);
  if (existing?.name) return existing.name;

  const discovered = await findExistingConversationFolder(
    rootHandle,
    attachment.conversationId
  );
  const name = discovered || buildMediaConversationFolderName(attachment);

  await putRecord(CONVERSATION_FOLDER_STORE, { key, name });
  return name;
};

const getFolderRecord = async inboxId =>
  getRecord(FOLDER_STORE, Number(inboxId));

export const getInboxFolderName = async inboxId => {
  const record = await getFolderRecord(inboxId);
  return record?.name || '';
};

export const getInboxStorageKind = async inboxId => {
  const folderRecord = await getFolderRecord(inboxId);
  if (!folderRecord?.handle) return 'browser_cache';
  if (!folderRecord.handle.queryPermission) return 'browser_cache';

  const permission = await folderRecord.handle.queryPermission({
    mode: 'readwrite',
  });
  return permission === 'granted' ? 'local_folder' : 'browser_cache';
};

const ensureFolderPermission = async handle => {
  if (!handle?.queryPermission) return false;

  const existing = await handle.queryPermission({ mode: 'readwrite' });
  if (existing === 'granted') return true;

  const requested = await handle.requestPermission({ mode: 'readwrite' });
  return requested === 'granted';
};

export const selectInboxFolder = async inboxId => {
  if (!isFileSystemAccessSupported()) return '';

  const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
  await putRecord(FOLDER_STORE, {
    inboxId: Number(inboxId),
    name: handle.name,
    handle,
    updatedAt: new Date().toISOString(),
  });
  completedAttachmentStorage.forEach((value, attachmentId) => {
    if (value.inboxId === Number(inboxId)) {
      completedAttachmentStorage.delete(attachmentId);
    }
  });
  mediaSyncDeviceStates[inboxId] = {
    ...(mediaSyncDeviceStates[inboxId] || {}),
    folderSelected: true,
    storageKind: 'local_folder',
  };
  if (navigator.storage?.persist) await navigator.storage.persist();
  return handle.name;
};

const writeBlobToFolder = async (inboxId, attachment, blob) => {
  if (!isFileSystemAccessSupported()) return false;

  const folderRecord = await getFolderRecord(inboxId);
  if (!folderRecord?.handle) return false;

  const hasPermission = await ensureFolderPermission(folderRecord.handle);
  if (!hasPermission) return false;

  const conversationFolderName = await getConversationFolderName(
    folderRecord.handle,
    attachment
  );
  const conversationFolder = await folderRecord.handle.getDirectoryHandle(
    conversationFolderName,
    { create: true }
  );
  const createdAt = new Date(attachment.createdAt || Date.now());
  const monthFolder = await conversationFolder.getDirectoryHandle(
    `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`,
    { create: true }
  );
  const fileHandle = await monthFolder.getFileHandle(safeFileName(attachment), {
    create: true,
  });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
  return true;
};

const checksumForBlob = async (blob, expectedChecksum = '') => {
  const buffer = await blob.arrayBuffer();
  if (expectedChecksum && expectedChecksum.length <= 24) {
    const hex = md5(new Uint8Array(buffer));
    const bytes = hex.match(/.{2}/g).map(value => parseInt(value, 16));
    return window.btoa(String.fromCharCode(...bytes));
  }

  const digest = await window.crypto.subtle.digest('SHA-256', buffer);
  const bytes = new Uint8Array(digest);
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary);
};

const updateStatus = async (
  inboxId,
  attachmentId,
  status,
  errorMessage = '',
  checksum = '',
  byteSize = null,
  storageKind = 'browser_cache'
) => {
  try {
    const deviceId = await getMediaSyncDeviceId();
    await InboxesAPI.updateMediaSyncStatuses(inboxId, [
      {
        attachment_id: attachmentId,
        status,
        error_message: errorMessage,
        checksum,
        byte_size: byteSize,
        device_id: deviceId,
        storage_kind: storageKind,
      },
    ]);
    return true;
  } catch {
    // Keep the attachment eligible for retry when the server is unavailable.
    return false;
  }
};

export const ensureAttachmentCached = async ({ inbox, attachment }) => {
  if (!inbox?.media_sync_enabled) return '';

  const normalized = normalizeAttachment(attachment);
  if (!normalized.attachmentId) return '';

  const completedStorage = completedAttachmentStorage.get(
    normalized.attachmentId
  );
  if (
    localMediaUrls[normalized.attachmentId] &&
    completedStorage?.inboxId === Number(inbox.id)
  ) {
    return localMediaUrls[normalized.attachmentId];
  }

  if (inFlightAttachments.has(normalized.attachmentId)) return '';
  inFlightAttachments.add(normalized.attachmentId);

  try {
    const cached = await getRecord(MEDIA_STORE, normalized.attachmentId);
    if (cached?.blob) {
      const checksum =
        cached.checksum ||
        (await checksumForBlob(cached.blob, normalized.checksum));
      const localUrl = objectUrlForBlob(normalized.attachmentId, cached.blob);
      const savedToFolder = await writeBlobToFolder(
        inbox.id,
        { ...normalized, ...cached },
        cached.blob
      );
      const storageKind = savedToFolder ? 'local_folder' : 'browser_cache';
      mediaSyncDeviceStates[inbox.id] = {
        ...(mediaSyncDeviceStates[inbox.id] || {}),
        folderSelected: savedToFolder,
        storageKind,
      };
      const statusReported = await updateStatus(
        inbox.id,
        normalized.attachmentId,
        'cached',
        '',
        checksum,
        cached.byteSize,
        storageKind
      );
      if (statusReported) {
        completedAttachmentStorage.set(normalized.attachmentId, {
          inboxId: Number(inbox.id),
          storageKind,
        });
      }
      return localUrl;
    }

    if (!normalized.downloadUrl) return '';

    const response = await fetch(normalized.downloadUrl, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Download failed with ${response.status}`);
    }

    const blob = await response.blob();
    const checksum = await checksumForBlob(blob, normalized.checksum);
    if (normalized.checksum && checksum !== normalized.checksum) {
      throw new Error('Downloaded media checksum does not match the server');
    }

    await putRecord(MEDIA_STORE, {
      ...normalized,
      blob,
      checksum,
      byteSize: blob.size,
      cachedAt: new Date().toISOString(),
    });
    const savedToFolder = await writeBlobToFolder(inbox.id, normalized, blob);
    const storageKind = savedToFolder ? 'local_folder' : 'browser_cache';
    mediaSyncDeviceStates[inbox.id] = {
      ...(mediaSyncDeviceStates[inbox.id] || {}),
      folderSelected: savedToFolder,
      storageKind,
    };
    const statusReported = await updateStatus(
      inbox.id,
      normalized.attachmentId,
      'cached',
      '',
      checksum,
      blob.size,
      storageKind
    );
    if (statusReported) {
      completedAttachmentStorage.set(normalized.attachmentId, {
        inboxId: Number(inbox.id),
        storageKind,
      });
    }

    return objectUrlForBlob(normalized.attachmentId, blob);
  } catch (error) {
    await updateStatus(
      inbox.id,
      normalized.attachmentId,
      'failed',
      error.message || 'Unable to cache media'
    );
    return '';
  } finally {
    inFlightAttachments.delete(normalized.attachmentId);
  }
};

export const enqueueAttachmentCache = ({ inbox, attachment }) => {
  return ensureAttachmentCached({ inbox, attachment });
};

export const cacheMessages = async messages => {
  const records = messages
    .filter(message => message?.id)
    .map(message => ({
      ...message,
      messageId: message.id,
      conversationId: message.conversation_id,
      cachedAt: new Date().toISOString(),
    }));

  await putRecords(MESSAGE_STORE, records);
};

export const getCachedMessages = async (
  conversationId,
  { before, after } = {}
) => {
  const messages = await getAllRecords(MESSAGE_STORE);
  return messages
    .filter(
      message => Number(message.conversationId) === Number(conversationId)
    )
    .filter(message => !before || Number(message.id) < Number(before))
    .filter(message => !after || Number(message.id) > Number(after))
    .sort((a, b) => Number(a.id) - Number(b.id));
};

export const registerPrimaryMediaSyncDevice = async inboxId => {
  if (navigator.storage?.persist) await navigator.storage.persist();
  const [deviceId, storageKind] = await Promise.all([
    getMediaSyncDeviceId(),
    getInboxStorageKind(inboxId),
  ]);
  const response = await InboxesAPI.registerMediaSyncDevice(inboxId, {
    device_id: deviceId,
    storage_kind: storageKind,
  });
  mediaSyncDeviceStates[inboxId] = {
    ...(mediaSyncDeviceStates[inboxId] || {}),
    folderSelected: storageKind === 'local_folder',
    storageKind,
    isPrimary: true,
    primaryConfigured: true,
  };
  return response.data;
};

export const loadMediaSyncDeviceState = async inboxId => {
  const storageKind = await getInboxStorageKind(inboxId);
  if (storageKind === 'browser_cache') {
    completedAttachmentStorage.forEach((value, attachmentId) => {
      if (value.inboxId === Number(inboxId)) {
        completedAttachmentStorage.delete(attachmentId);
      }
    });
  }
  mediaSyncDeviceStates[inboxId] = {
    ...(mediaSyncDeviceStates[inboxId] || {}),
    folderSelected: storageKind === 'local_folder',
    storageKind,
  };
  return mediaSyncDeviceStates[inboxId];
};

const cacheAttachments = async (inbox, attachments, concurrency = 4) => {
  let nextIndex = 0;
  const workers = Array.from(
    { length: Math.min(concurrency, attachments.length) },
    async () => {
      while (nextIndex < attachments.length) {
        const attachment = attachments[nextIndex];
        nextIndex += 1;
        // Each worker processes one download at a time to avoid flooding the VPS.
        // eslint-disable-next-line no-await-in-loop
        await ensureAttachmentCached({ inbox, attachment });
      }
    }
  );
  await Promise.all(workers);
};

const syncMessageManifest = async (inbox, force) => {
  const syncRecord = await getRecord(SYNC_STORE, Number(inbox.id));
  const params = { limit: MANIFEST_LIMIT };
  if (!force && syncRecord?.messageSyncedAt) {
    params.since = syncRecord.messageSyncedAt;
  }

  let cursor;
  let generatedAt;
  do {
    if (cursor) params.cursor = cursor;
    // The manifest is paginated and each page must be persisted before fetching the next.
    // eslint-disable-next-line no-await-in-loop
    const response = await InboxesAPI.getMessageSyncManifest(inbox.id, params);
    const payload = response.data?.payload || [];
    // eslint-disable-next-line no-await-in-loop
    await cacheMessages(payload);

    const meta = response.data?.meta || {};
    generatedAt = generatedAt || meta.generated_at;
    cursor = meta.has_more ? meta.next_cursor : null;
  } while (cursor);

  await putRecord(SYNC_STORE, {
    inboxId: Number(inbox.id),
    messageSyncedAt: generatedAt || new Date().toISOString(),
  });
};

export const syncInboxManifest = async (inbox, { force = false } = {}) => {
  if (!inbox?.id || !inbox.media_sync_enabled) return Promise.resolve();

  const lastSyncedAt = syncedInboxes.get(inbox.id) || 0;
  if (!force && Date.now() - lastSyncedAt < SYNC_INTERVAL) {
    return Promise.resolve();
  }
  if (syncInFlight.has(inbox.id)) return syncInFlight.get(inbox.id);

  syncedInboxes.set(inbox.id, Date.now());
  const syncMediaManifest = async () => {
    const deviceId = await getMediaSyncDeviceId();
    let cursor;
    let availableMedia = 0;
    let latestMeta = {};
    do {
      // eslint-disable-next-line no-await-in-loop
      const response = await InboxesAPI.getMediaSyncManifest(inbox.id, {
        limit: MANIFEST_LIMIT,
        cursor,
        device_id: deviceId,
      });
      const attachments = response.data?.payload || [];
      latestMeta = response.data?.meta || {};
      availableMedia += attachments.length;
      // eslint-disable-next-line no-await-in-loop
      await cacheAttachments(inbox, attachments);
      cursor = latestMeta.has_more ? latestMeta.next_cursor : null;
    } while (cursor);

    syncSummaries[inbox.id] = {
      ...(latestMeta.sync_status || {}),
      availableMedia,
    };
    mediaSyncDeviceStates[inbox.id] = {
      ...(mediaSyncDeviceStates[inbox.id] || {}),
      isPrimary: latestMeta.current_device_is_primary,
      primaryConfigured: latestMeta.primary_device_configured,
    };
    return latestMeta;
  };

  const syncPromise = Promise.all([
    syncMessageManifest(inbox, force),
    syncMediaManifest(),
  ]).finally(() => syncInFlight.delete(inbox.id));

  syncInFlight.set(inbox.id, syncPromise);

  if (!syncTimers.has(inbox.id)) {
    const timer = window.setInterval(() => {
      syncInboxManifest(inbox).catch(() => {});
    }, SYNC_INTERVAL);
    syncTimers.set(inbox.id, timer);
  }

  return syncPromise;
};

export const stopInboxMediaSync = inboxId => {
  const timer = syncTimers.get(inboxId);
  if (timer) window.clearInterval(timer);
  syncTimers.delete(inboxId);
  syncedInboxes.delete(inboxId);
};
